import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faPhoneAlt, faPhoneSlash, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useIpcContactName } from '../../hooks/useIpcContactName';

import Auxi from '../../hoc/Auxi';
import AppLayout from '../Layout/AppLayout';
import { ToastContainer, toast } from 'react-toastify';
import Spinner from '../../components/Widgets/Spinner';
import MfaAuthModal from '../../components/Widgets/MfaAuthModal';

import '../../assets/css/style.css';

const CallLogs = () => {
  // State declarations
  const [state, setState] = useState({
    recordType: 'vmail/new',
    voiceMails: [],
    username: "Daniyal",
    loading: false,
    audioSource: '',
    showModal: false,
    data: {},
    cdrs: null,
    rawCdrs: null,
    searchTerm: '',
    password: '',
    cloudId: '',
    uid: '',
    userDomain: '',
    uType: '',
    scope: '',
    showMyCDRs: false,
    loggedIn: false,
    url: '',
    ownContact: null,
    contacts: {},
    showRecordings: false,
    direct: false
  });

  const [filter, setFilter] = useState("all");
  const mfaAuthRef = useRef(null);

  const { getDisplayName, ipcInitialized } = useIpcContactName(state.cloudId, state.contacts);

  // Helper function to update state
  const updateState = (updates) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const inputChangeHandler = (evt) => {
    const target = evt.target;
    const name = target.name;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    
    if (value === true) value = "yes";
    if (value === false) value = "no";

    setState(prev => ({
      ...prev,
      data: { ...prev.data, [name]: value }
    }));
  };

  const fetchContacts = async (username, password, cloudId) => {
    try {
      const payLoad = {
        username: username,
        password: password,
        cloud_id: cloudId
      };
      
      const response = await axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/fetchContacts`, payLoad);
      const contactsLower = {};
      Object.entries(response.data).forEach(([key, value]) => {
        contactsLower[key.toLowerCase()] = value;
      });
      updateState({ contacts: contactsLower });
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Error loading contacts');
    }
  };

  const handleSearch = (event) => {
    const searchTerm = event.target.value;
    updateState({ searchTerm });

    // Use the effective scope based on the Show My Call History toggle
    const effectiveScope = state.showMyCDRs ? "My" : state.scope;

    // Re-render CDRs with search filter using the correct scope
    filterCDRS(
      state.rawCdrs,
      effectiveScope,
      state.username,
      state.userDomain,
      state.password,
      state.cloudId,
      state.showRecordings,
      searchTerm,
      filter
    );
  };

  const filterCDRS = (data, scope, userName, domain, password, cloudId, showRecordings, searchedInput, callTypeFilter = "all") => {
    const cdrs = data.map((row, key) => {
      // Filter by call type first
      if (callTypeFilter !== "all") {
        if (callTypeFilter === "Outbound" && row.type !== "0") {
          return null;
        }
        if (callTypeFilter === "Inbound" && row.type !== "1") {
          return null;
        }
        if (callTypeFilter === "Missed" && (row.type === "0" || row.type === "1")) {
          return null;
        }
      }

      let number = row.number;
      if (scope === "Office Manager"){
        if(row.type === "1"){
          number = row.orig_from_uri;
        } else {
          number = row.term_to_uri;
        }
      }
      if (!number) return null;
      
      if (number && number.includes("sip")) {
        number = number.split(":")[1];
        number = number.split("@")[0];
      }
      
      // Get display name using the hook
      const displayName = getDisplayName(number + "@"+domain);
      
      // Filter based on search input
      if (searchedInput && searchedInput.trim()) {
        const searchTerm = searchedInput.toLowerCase().trim();
        
        // Get contact from API contacts
        const apiContact = number ? state.contacts[number.toLowerCase()] : null;
        
        // Clean up name fields
        const cleanName = (name) => name ? name.replace(/\s+/g, ' ').trim().toLowerCase() : '';
        
        // Collect all searchable fields
        const searchableFields = [
          cleanName(row.name),
          cleanName(row.CdrR?.orig_from_name),
          cleanName(row.orig_from_name),
          displayName && displayName !== 'undefined' ? cleanName(displayName) : '',
          number ? number.toLowerCase() : '',
          apiContact ? cleanName(apiContact) : '',
          row.orig_sub ? row.orig_sub.toLowerCase() : '',
          row.term_sub ? row.term_sub.toLowerCase() : ''
        ].filter(field => field);
        
        // Check if any field matches the search term
        const hasMatch = searchableFields.some(field => {
          // Direct match
          if (field.includes(searchTerm)) {
            return true;
          }
          
          // Word-based match
          const searchWords = searchTerm.split(' ');
          for (const word of searchWords) {
            if (word.length >= 2 && field.includes(word)) {
              return true;
            }
          }
          
          // Partial word match
          const fieldWords = field.split(' ');
          for (const searchWord of searchWords) {
            if (searchWord.length < 2) continue;
            
            for (const fieldWord of fieldWords) {
              if (fieldWord.startsWith(searchWord) || 
                  fieldWord.endsWith(searchWord) || 
                  searchWord.startsWith(fieldWord) || 
                  searchWord.endsWith(fieldWord)) {
                return true;
              }
            }
          }
          
          return false;
        });
        
        // If no match, don't render this CDR
        if (!hasMatch) {
          return null;
        }
      }

      let icons = null;
      if (row.type === "0") {
        // outbound
        icons = (
          <Auxi>
            <div className="call-status-top outbound-icon">
              <FontAwesomeIcon icon={faArrowUp} style={{marginRight:"10px"}}/>
            </div>
            <div className="call-status-down outbound-icon">
              <FontAwesomeIcon icon={faPhoneAlt} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      } else if (row.type === "1") {
        // inbound
        icons = (
          <Auxi>
            <div className="call-status-top inbound-icon">
              <FontAwesomeIcon icon={faArrowUp} style={{marginRight:"10px", transform: "rotate(180deg)"}}/>
            </div>
            <div className="call-status-down inbound-icon">
              <FontAwesomeIcon icon={faPhoneAlt} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      } else {
        // missed
        icons = (
          <Auxi>
            <div className="call-status-top missed-icon"></div>
            <div className="call-status-down missed-icon">
              <FontAwesomeIcon icon={faPhoneSlash} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      }

      const unixDate = moment.unix(row.time_start).toDate();
      const callDate = moment(unixDate).local().format('MM/DD, h:mm a');

      return (
        <Auxi key={key}>
          <div className="call-logs-container" onClick={() => toggleIcons(`tableRow${key}`)}>
            {icons}
            <div className="call-name">
              {displayName !== number? displayName: row.name || row.orig_from_name || number} {scope === "Office Manager" ? " - (" + (row.orig_sub || row.term_sub) + ")" : null}
            </div>
            <div className="call-number">
              {(displayName !== number) || row.name || row.orig_from_name ? number : null}
            </div>
            <div className="call-date">
              {callDate}
            </div>
            <div className="call-duration">
              {moment.utc(row.duration*1000).format('HH:mm:ss')}
            </div>
          </div>
          <div className="cdr-icons">
            <div className="flex justify-between w-full" style={{display:"none"}} id={`tableRow${key}`}>
              <div className="">
                <button 
                  className="cdr-custom-btns btn btn-dark btn-block"
                  onClick={() => makeCall(number)}
                >
                  Call
                </button>
              </div>
              <div className="">
                <button 
                  className="cdr-custom-btns btn btn-dark btn-block"
                  onClick={(evt) => messageToNumber(number)}
                >
                  Message
                </button>
              </div>
              {
                (scope === "Office Manager" || showRecordings) && (
                  <div className="">
                    <button 
                      className="cdr-custom-btns btn btn-dark btn-block"
                      onClick={() => getCallRecording(row.CdrR.orig_callid,  userName, domain, password, cloudId, scope)}
                      style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                    >
                      Recording
                    </button>
                  </div> 
                )
              }
              
              
              
            </div>
            <div className="w-full mt-3" data-callid={row.CdrR.orig_callid}>
              <div className="recording-player" style={{display: 'none', width: '100%'}}></div>
            </div>
          </div>
          
          <hr/>
        </Auxi>
      );
    });

    updateState({ cdrs });
  };

  const createCDRS = (data, scope, userName, domain, password, cloudId, showRecordings, callTypeFilter = "all") => {
    const cdrs = data.map((row, key) => {
      // Filter by call type first
      if (callTypeFilter !== "all") {
        if (callTypeFilter === "Outbound" && row.type !== "0") {
          return null;
        }
        if (callTypeFilter === "Inbound" && row.type !== "1") {
          return null;
        }
        if (callTypeFilter === "Missed" && (row.type === "0" || row.type === "1")) {
          return null;
        }
      }

      let number = row.number;
      if (scope === "Office Manager"){
        if(row.type === "1"){
          number = row.orig_from_uri;
        } else {
          number = row.term_to_uri;
        }
      }
      if (!number) return null;
      
      if (number && number.includes("sip")) {
        number = number.split(":")[1];
        number = number.split("@")[0];
      }
      
      // Get display name using the hook
      const displayName = getDisplayName(number + "@"+domain);

      let icons = null;
      if (row.type === "0") {
        // outbound
        icons = (
          <Auxi>
            <div className="call-status-top outbound-icon">
              <FontAwesomeIcon icon={faArrowUp} style={{marginRight:"10px"}}/>
            </div>
            <div className="call-status-down outbound-icon">
              <FontAwesomeIcon icon={faPhoneAlt} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      } else if (row.type === "1") {
        // inbound
        icons = (
          <Auxi>
            <div className="call-status-top inbound-icon">
              <FontAwesomeIcon icon={faArrowUp} style={{marginRight:"10px", transform: "rotate(180deg)"}}/>
            </div>
            <div className="call-status-down inbound-icon">
              <FontAwesomeIcon icon={faPhoneAlt} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      } else {
        // missed
        icons = (
          <Auxi>
            <div className="call-status-top missed-icon"></div>
            <div className="call-status-down missed-icon">
              <FontAwesomeIcon icon={faPhoneSlash} style={{marginRight:"10px"}}/>
            </div>
          </Auxi>
        );
      }

      const unixDate = moment.unix(row.time_start).toDate();
      const callDate = moment(unixDate).local().format('MM/DD, h:mm a');

      return (
        <Auxi key={key}>
          <div className="call-logs-container" onClick={() => toggleIcons(`tableRow${key}`)}>
            {icons}
            <div className="call-name">
              {displayName !== number? displayName: row.name || row.orig_from_name || number} {scope === "Office Manager" ? " - (" + (row.orig_sub || row.term_sub) + ")" : null}
            </div>
            <div className="call-number">
              {(displayName !== number) || row.name || row.orig_from_name ? number : null}
            </div>
            <div className="call-date">
              {callDate}
            </div>
            <div className="call-duration">
              {moment.utc(row.duration*1000).format('HH:mm:ss')}
            </div>
          </div>
          <div className="cdr-icons">
            <div className="flex justify-between w-full" style={{display:"none"}} id={`tableRow${key}`}>
              <div className="">
                <button 
                  className="cdr-custom-btns btn btn-dark btn-block"
                  onClick={() => makeCall(number)}
                >
                  Call
                </button>
              </div>
              <div className="">
                <button 
                  className="cdr-custom-btns btn btn-dark btn-block"
                  onClick={(evt) => messageToNumber(number)}
                >
                  Message
                </button>
              </div>
              {
                (scope === "Office Manager" || showRecordings) && (
                  <div className="">
                    <button 
                      className="cdr-custom-btns btn btn-dark btn-block"
                      onClick={() => getCallRecording(row.CdrR.orig_callid,  userName, domain, password, cloudId, scope)}
                      style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                    >
                      Recording
                    </button>
                  </div> 
                )
              }
              
              
              
            </div>
            <div className="w-full mt-3" data-callid={row.CdrR.orig_callid}>
              <div className="recording-player" style={{display: 'none', width: '100%'}}></div>
            </div>
          </div>
          
          <hr/>
        </Auxi>
      );
    });

    updateState({ cdrs });
  };
  

  const getCDRs = async (uType, userName, domain, password, cloudId, scope, showRecordings) => {
    // console.log("showRecordings", showRecordings)
    try {
      const x = new Date();
      const endDate = moment(x).format('YYYY-MM-DD');
      x.setMonth(x.getMonth() - 6);
      const startDate = moment(x).format('YYYY-MM-DD');

      const payLoad = new FormData();
      if (scope !== "Office Manager") {
        payLoad.append('uid', userName);
      }
      payLoad.append('start_date', startDate);
      payLoad.append('end_date', endDate);
      payLoad.append('cloud_id', cloudId);
      payLoad.append('domain', domain);

      updateState({ loading: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}?format=json&object=cdr2&action=read&utype=u&cloud_username=${userName}&cloud_password=${password}`,
        payLoad
      );
      updateState({ loading: false, rawCdrs: response.data });

      // Apply both filter and search after data is loaded
      // Note: scope parameter already represents the effective scope (either "My" or original scope)
      filterCDRS(
        response.data,
        scope,
        userName,
        domain,
        password,
        cloudId,
        showRecordings,
        state.searchTerm,
        filter
      );
    } catch (err) {
      toast.error(err.message);
      updateState({ loading: false });
    }
  };

  const toggleIcons = (id) => {
    const elem = document.getElementById(id);
    if (elem) {
      elem.style.display = window.getComputedStyle(elem).display === "none" ? "flex" : "none";
    }
  };

  const makeCall = (number) => {
    window.location.href = `sip:${number}?dialAction=autoCall`;
  };

  const messageToNumber = (number) => {
    // Get current URL parameters
    // const currentUrl = new URL(window.location.href);
    // const username = currentUrl.searchParams.get('username');
    // const password = currentUrl.searchParams.get('password');
    // const cloudId = currentUrl.searchParams.get('cloud_id')?.replace("*", "");
    // const queryString = `username=${username}&password=${password}&cloud_id=${cloudId}`;
    
    // window.location.href = `app:///history?${queryString}&new_message=true&to_user=${number}`;
  
    const currentUrl = new URL(window.location.href);
    const username = currentUrl.searchParams.get('username');
    const password = currentUrl.searchParams.get('password');
    const cloudId = currentUrl.searchParams.get('cloud_id')?.replace("*", "");
    const queryString = `username=${username}&password=${password}&cloud_id=${cloudId}`;
    const relative = `?${queryString}&new_message=true&to_user=${number}`;
    let tabName = "conferencing";
    if (cloudId == "CREXDEMO"){
      tabName = "history";
    }
    window.location.href = `app:?action=navigate&tab=${tabName}&relative=${encodeURIComponent(relative)}`;
  
  
  };

  const getCallRecording = async (callid, userName, domain, password, cloudId, scope) => {
    try {

      const payload = {
        cloud_username: userName,
        cloud_password: password,
        cloud_id: cloudId,
        call_id: callid
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/call_recording`, payload);
      
      if (response.data) {
        const recordingUrl = response.data['file-access-url'];
        
        if (recordingUrl) {
          // Show player under the recording button
          const recordingButton = document.querySelector(`[data-callid="${callid}"]`);
          const playerContainer = recordingButton.parentElement.querySelector('.recording-player');
          
          // Create the audio player with multiple sources
          playerContainer.innerHTML = `
            <div style="margin-top: 10px; padding: 10px; background: #f8f9fa; border-radius: 5px;">
              <audio style="width: 100%" controls>
                <source src="${recordingUrl}&convert=wav16" type="audio/wav"/>
                <source src="${recordingUrl}&convert=aac" type="audio/aac"/>
                <source src="${recordingUrl}&convert=aac" type="audio/mp4"/>
                Your browser does not support the audio element.
              </audio>
            </div>
          `;
          playerContainer.style.display = 'block';
        } else {
          toast.error('No recording available for this call');
        }
      } else {
        toast.error('No recording found for this call');
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error('No recording available for this call');
      } else {
        console.error('Error fetching call recording:', error);
        toast.error('Error loading recording');
      }
    }
  };

  const downloadAndPlayAudio = async (callid, recordingUrl) => {
    try {
      // Show loading state
      const recordingButton = document.querySelector(`[data-callid="${callid}"]`);
      const playerContainer = recordingButton.parentElement.querySelector('.recording-player');
      
      playerContainer.innerHTML = `
        <div style="padding: 10px; text-align: center;">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <span style="margin-left: 10px;">Downloading audio...</span>
        </div>
      `;
      playerContainer.style.display = 'block';

      // Download the audio file directly
      const response = await axios.get(recordingUrl, {
        responseType: 'blob'
      });

      // Convert blob to array buffer
      const arrayBuffer = await response.data.arrayBuffer();
      // console.log('Array buffer size:', arrayBuffer.byteLength);
      
      // Check the WAV header details
      const dataView = new DataView(arrayBuffer);
      const signature = String.fromCharCode(
        dataView.getUint8(0),
        dataView.getUint8(1),
        dataView.getUint8(2),
        dataView.getUint8(3)
      );
      // console.log('File signature:', signature);

      if (signature === 'RIFF') {
        // Read WAV format details
        const formatChunkOffset = 12; // After RIFF + size + WAVE
        const formatSignature = String.fromCharCode(
          dataView.getUint8(formatChunkOffset),
          dataView.getUint8(formatChunkOffset + 1),
          dataView.getUint8(formatChunkOffset + 2),
          dataView.getUint8(formatChunkOffset + 3)
        );

        const audioFormat = dataView.getUint16(20, true);
        const numChannels = dataView.getUint16(22, true);
        const sampleRate = dataView.getUint32(24, true);
        const byteRate = dataView.getUint32(28, true);
        const blockAlign = dataView.getUint16(32, true);
        const bitsPerSample = dataView.getUint16(34, true);

        console.log('WAV details:', {
          audioFormat,    // 1 = PCM, other values indicate compression
          numChannels,    // 1 = mono, 2 = stereo
          sampleRate,     // samples per second
          byteRate,       // bytes per second
          blockAlign,     // bytes per sample * num channels
          bitsPerSample   // bits per sample
        });
      }
      
      // Create blob URL with appropriate content type
      let contentType;
      if (signature === 'RIFF') {
        contentType = 'audio/wav';
      } else if (signature === 'ID3\x03' || signature === 'ID3\x02') {
        contentType = 'audio/mpeg';
      } else {
        // Try to detect format from response headers
        contentType = response.headers['content-type'] || 'audio/wav';
      }
      
      // Try to fix WAV header if needed
      let finalBuffer = arrayBuffer;
      if (contentType === 'audio/wav' && signature !== 'RIFF') {
        const wavHeader = new ArrayBuffer(44); // Standard WAV header size
        const wavView = new DataView(wavHeader);
        
        // Write WAV header
        wavView.setUint32(0, 0x52494646, false); // 'RIFF'
        wavView.setUint32(4, 36 + arrayBuffer.byteLength, true); // File size
        wavView.setUint32(8, 0x57415645, false); // 'WAVE'
        wavView.setUint32(12, 0x666D7420, false); // 'fmt '
        wavView.setUint32(16, 16, true); // Format chunk size
        wavView.setUint16(20, 1, true); // Audio format (PCM)
        wavView.setUint16(22, 1, true); // Num channels (mono)
        wavView.setUint32(24, 8000, true); // Sample rate
        wavView.setUint32(28, 16000, true); // Byte rate
        wavView.setUint16(32, 2, true); // Block align
        wavView.setUint16(34, 16, true); // Bits per sample
        wavView.setUint32(36, 0x64617461, false); // 'data'
        wavView.setUint32(40, arrayBuffer.byteLength, true); // Data size
        
        // Combine header with data
        const finalArray = new Uint8Array(wavHeader.byteLength + arrayBuffer.byteLength);
        finalArray.set(new Uint8Array(wavHeader), 0);
        finalArray.set(new Uint8Array(arrayBuffer), wavHeader.byteLength);
        finalBuffer = finalArray.buffer;
      }
      
      const blob = new Blob([finalBuffer], { type: contentType });
      const audioBlobUrl = URL.createObjectURL(blob);

      // Create audio player with the blob URL programmatically
      playerContainer.innerHTML = '';
      
      // Create container div
      const containerDiv = document.createElement('div');
      containerDiv.className = 'recording-player-container';
      containerDiv.style.cssText = 'margin-top: 10px; padding: 10px; background: #f8f9fa; border-radius: 5px;';
      
      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = 'margin-bottom: 10px;';
      
      // Create download button
      const downloadButton = document.createElement('button');
      downloadButton.className = 'btn btn-sm btn-primary';
      downloadButton.textContent = 'Download Recording';
      downloadButton.onclick = () => {
        const link = document.createElement('a');
        link.href = audioBlobUrl;
        link.download = `recording_${callid}.wav`;
        link.click();
      };

      // Get audio format from WAV header if present
      const audioFormat = signature === 'RIFF' ? dataView.getUint16(20, true) : null;
      const isFormatSupported = signature === 'RIFF' && audioFormat === 1; // PCM WAV only
      
      if (!isFormatSupported) {
        // Show format info and download-only message
        const infoDiv = document.createElement('div');
        infoDiv.style.cssText = 'margin-top: 10px; color: #666; font-size: 0.9em;';
        infoDiv.textContent = 'This recording is in a compressed format (GSM). Please download to play in a media player.';
        
        buttonContainer.appendChild(downloadButton);
        containerDiv.appendChild(buttonContainer);
        containerDiv.appendChild(infoDiv);
      } else {
        // Create audio element for supported formats
        const audioElement = document.createElement('audio');
        audioElement.controls = true;
        audioElement.style.cssText = 'width: 100%;';
      
        // Add error handling
        audioElement.onerror = (e) => {
          console.error('Audio error:', e);
          console.error('Audio error details:', {
            error: audioElement.error,
            networkState: audioElement.networkState,
            readyState: audioElement.readyState,
            errorCode: audioElement.error?.code,
            errorMessage: audioElement.error?.message
          });
          
          // If playback fails, switch to download-only mode
          containerDiv.innerHTML = '';
          const infoDiv = document.createElement('div');
          infoDiv.style.cssText = 'margin-bottom: 10px; color: #666; font-size: 0.9em;';
          infoDiv.textContent = 'Unable to play this recording in the browser. Please download to play in a media player.';
          
          containerDiv.appendChild(infoDiv);
          containerDiv.appendChild(downloadButton);
        };
        
        audioElement.onloadeddata = () => {
          console.log('Audio loaded successfully');
        };
        
        audioElement.src = audioBlobUrl;
        audioElement.load();
        
        // Add fallback text
        audioElement.textContent = 'Your browser does not support the audio element.';
        
        buttonContainer.appendChild(downloadButton);
        containerDiv.appendChild(buttonContainer);
        containerDiv.appendChild(audioElement);
      }
      
      playerContainer.appendChild(containerDiv);

    } catch (error) {
      console.error('Error downloading audio:', error);
      const playerContainer = document.querySelector(`[data-callid="${callid}"]`).parentElement.querySelector('.recording-player');
      playerContainer.innerHTML = `
        <div style="padding: 10px; color: red; text-align: center;">
          Error loading audio. Please try again.
        </div>
      `;
    }
  };

  const showRecordingPlayer = (callid, recordingUrl) => {
    downloadAndPlayAudio(callid, recordingUrl);
  };

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const url = new URL(window.location.href);
        let username = url.searchParams.get('username');
        let password = url.searchParams.get('password');
        let cloudId = url.searchParams.get('cloud_id');
        const direct = url.searchParams.get('direct') === '1';
        const mUrl = url.href.substring(url.href.lastIndexOf("?username="));
        
        cloudId = cloudId.replace("*", "");
        updateState({
          url: mUrl,
          username,
          password,
          cloudId,
          direct
        });

        const payLoad = { user: username, password, cloud_id: cloudId };
        let response = await axios.post(`${process.env.REACT_APP_API_URL}/signin`, payLoad);
        
        if (response.data.sso === "sso_required") {
          const completed = await mfaAuthRef.current?.openAndAuthenticate({
            authType: 'sso',
            scope: response.data.scope,
            cloudId: cloudId
          });
          if (!completed) {
            updateState({ loading: false });
            return;
          }
          response = await axios.post(`${process.env.REACT_APP_API_URL}/signin`, payLoad);
        }

        username = response.data.user + "@" + response.data.domain;

        if (response.data.mfa === "mfa_required") {
          const authenticated = await mfaAuthRef.current?.openAndAuthenticate({
            username: response.data.username || `${response.data.user}@${response.data.domain}`,
            cloudId: cloudId,
            password: password,
            mfaType: response.data.mfa_type,
            mfaVendor: response.data.mfa_vendor,
            accessToken: response.data.access_token
          });
          if (!authenticated) {
            updateState({ loading: false });
            return;
          }
        }
        
        // Check portal call history recording permissions
        const uiconfigPayload = new FormData();
        uiconfigPayload.append('domain', response.data.domain);
        uiconfigPayload.append('user', response.data.user);
        uiconfigPayload.append('config_name', 'PORTAL_CALL_HISTORY_SHOW_RECORDINGS');
        uiconfigPayload.append('cloud_id', cloudId);
        
        const uiconfigResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}?format=json&object=uiconfig&action=read&utype=u&cloud_username=${username}&cloud_password=${password}`,
          uiconfigPayload
        );
        
        const showRecordings = uiconfigResponse.data?.[0]?.config_value === "yes";

        // Fetch contacts once after successful sign-in
        await fetchContacts(username, password, cloudId);
        
        updateState({
          username,
          uid: response.data.uid,
          userDomain: response.data.domain,
          uType: 'u',
          scope: response.data.scope,
          loggedIn: true,
          showRecordings: showRecordings
        });
        
        await getCDRs('s', username, response.data.domain, password, cloudId, response.data.scope, showRecordings);
      } catch (err) {
        toast.error(`Error loading data: ${err.message}`);
        updateState({ loading: false });
      }
    };

    loadInitialData();
  }, []); // Run only once on mount

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && state.username && state.password && state.cloudId) {
        getCDRs(
          's',
          state.username,
          state.userDomain,
          state.password,
          state.cloudId,
          state.showMyCDRs ? "My" : state.scope,
          state.showRecordings
        );
        // Note: filter is preserved, only search term is cleared
        updateState({searchTerm: ''})
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [state.username, state.password, state.cloudId, state.userDomain, state.showMyCDRs, filter]); // Include filter in dependencies

  // Poll for new CDRs every 10 seconds
  useEffect(() => {
    if (!state.username || !state.password || !state.cloudId || !state.userDomain) {
      return;
    }

    const intervalId = setInterval(() => {
      // Only fetch when tab is visible
      if (document.visibilityState !== "visible") return;

      const effectiveScope = state.showMyCDRs ? "My" : state.scope;
      getCDRs(
        's',
        state.username,
        state.userDomain,
        state.password,
        state.cloudId,
        effectiveScope,
        state.showRecordings
      );
    }, 10000); // 10 seconds

    return () => clearInterval(intervalId);
  }, [
    state.username,
    state.password,
    state.cloudId,
    state.userDomain,
    state.scope,
    state.showMyCDRs,
    state.showRecordings,
    filter,
    state.searchTerm
  ]);


  const filterChangeHandler = (filterName) => {
    setFilter(filterName);
    
    // Re-filter CDRs with the new filter
    if (state.rawCdrs) {
      const effectiveScope = state.showMyCDRs ? "My" : state.scope;
      filterCDRS(
        state.rawCdrs,
        effectiveScope,
        state.username,
        state.userDomain,
        state.password,
        state.cloudId,
        state.showRecordings,
        state.searchTerm,
        filterName
      );
    }
  }

  return (
    <Auxi>
      {!ipcInitialized ? (
        <div className="flex w-full h-full items-center justify-center" style={{marginTop: "60px"}}>
          <Spinner data="" />
        </div>
      ) : (
        <>
          {state.loading && (
            <div style={{
              position: 'fixed',
              top: '15px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              padding: '10px 20px'
            }}>
              <Spinner data="" />
            </div>
          )}
          
          {!state.direct && (
            <AppLayout 
              cloud_id="SAEVOLGO"
              display_name={state.username}
              ownContact={state.ownContact}
              url={state.url}
            />
          )}
          <div className="CustomMainIndexApp" style={{marginBottom: "10px", marginTop: "10px"}}>
            <div style={{
              position: 'fixed',
              width: '100%',
              top: state.direct ? '0px' : '50px',
              left: '0px',
              right: '15px',
              zIndex: 209,
              background: 'white',
              padding: '0 15px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
             
                {/* <input
                  type="text"
                  placeholder="Search calls..."
                  value={state.searchTerm}
                  onChange={handleSearch}
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                /> */}

                <div className="row search">
                    <div className = "col-md-12 search-container"> 
                        <input type="text"  className="form-control"
                            placeholder="Search calls..."
                            onChange={handleSearch} 
                            value={state.searchTerm}
                        />
                        <span> <FontAwesomeIcon icon={faSearch} /></span>
                    </div>
                </div>

                <div className="row filter-container">
                    <div className = {filter === "all"?"col-3 col-md-3 filter active-filter":"col-3 col-md-3 filter"}
                        onClick={e => filterChangeHandler("all")}> 
                        <label>All</label>
                    </div>
                    <div className = {filter === "Missed"?"col-3 col-md-3 filter active-filter":"col-3 col-md-3 filter"}
                        onClick={e => filterChangeHandler("Missed")}> 
                        <label>Missed</label>
                    </div>
                    <div className = {filter === "Inbound"?"col-3 col-md-3 filter active-filter":"col-3 col-md-3 filter"}
                        onClick={e => filterChangeHandler("Inbound")}> 
                        <label>Inbound</label>
                    </div>
                    <div className = {filter === "Outbound"?"col-3 col-md-3 filter active-filter":"col-3 col-md-3 filter"}
                        onClick={e => filterChangeHandler("Outbound")}> 
                        <label>Outbound</label>
                    </div>
                </div>
              {(state.scope === "Office Manager") && (
                <div style={{
                  padding: '0 0px 10px',
                  borderBottom: '1px solid #edf2f7'
                }}>
                  <label className="checkbox-container" style={{display: "flex", alignItems: "center"}}>
                    <input
                      type="checkbox"
                      checked={state.showMyCDRs}
                      onChange={(e) => {
                        updateState({ showMyCDRs: e.target.checked });
                        const newScope = e.target.checked ? "My" : state.scope;
                        getCDRs(
                          's',
                          state.username,
                          state.userDomain,
                          state.password,
                          state.cloudId,
                          newScope,
                          state.showRecordings
                        );
                      }}
                      style={{marginRight: "8px"}}
                    />
                      Show My Call History
                  </label>
                </div>
              )}
            </div>
            
            <div className="row voice-message-layout" style={{
              marginTop: state.direct
                ? (state.scope === "Office Manager" ? "195px" : "150px")
                : (state.scope === "Office Manager" ? "245px" : "200px")
            }}>
            {/* <div style={{marginTop: "40px", background: "black", width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
              <audio
                style={{width: "100%"}}
                controls
                >
                   <source src="https://recording2-iad.vipvoice.io/ns-api/?object=recording&action=play&accel=sip:nms@core3-ord.vipvoice.io&casid=120@Saevolgo.ca&cccid=8EE5&calid=7h8ec3db6tb2c1amf7ku&uid=recording2-iad.vipvoice.io&time=20251001191906&auth=69eea6065a3510a0751b73266e9958ea&convert=wav16" type="audio/wav"/>
                  <source src="https://recording2-iad.vipvoice.io/ns-api/?object=recording&action=play&accel=sip:nms@core3-ord.vipvoice.io&casid=120@Saevolgo.ca&cccid=8EE5&calid=7h8ec3db6tb2c1amf7ku&uid=recording2-iad.vipvoice.io&time=20251001191906&auth=69eea6065a3510a0751b73266e9958ea&convert=aac" type="audio/aac"/>
                  <source src="https://recording2-iad.vipvoice.io/ns-api/?object=recording&action=play&accel=sip:nms@core3-ord.vipvoice.io&casid=120@Saevolgo.ca&cccid=8EE5&calid=7h8ec3db6tb2c1amf7ku&uid=recording2-iad.vipvoice.io&time=20251001191906&auth=69eea6065a3510a0751b73266e9958ea&convert=aac" type="audio/mp4"/>
                </audio>
            </div> */}
              {state.cdrs}
            </div>
          </div>
          <ToastContainer />
          <MfaAuthModal ref={mfaAuthRef} />
        </>
      )}
    </Auxi>
  );
};

export default CallLogs;
